import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setDataLayout,
  setDataTheme,
} from "../../data/redux/themeSettingSlice";
import ImageWithBasePath from "../imageWithBasePath";
import {
  setExpandMenu,
  setMobileSidebar,
  toggleMiniSidebar,
} from "../../data/redux/sidebarSlice";
import { useState, useEffect, useRef, useCallback } from "react";
import { all_routes } from "../../../feature-module/router/all_routes";
import { useLogoutMutation } from "../../../feature-module/auth/login/api/loginApi";
import {
  logout,
  selectUserRole,
  selectCurrentUser,
} from "../../data/slice/authSlice";
import {
  useGetAllNotificationsQuery,
  useMarkNotificationAsReadMutation,
} from "../../../feature-module/notifications/api/notificationApi";
import LockScreenButton from "../../../feature-module/auth/LockScreenButton";
import { useGetCenterByIdQuery } from "../../../feature-module/peoples/centers/api/centerApi";

const Header = () => {
  // Initialize routes and Redux dispatch
  const routes = all_routes;
  const dispatch = useDispatch();
  const [logoutMutation] = useLogoutMutation();

  // Get theme and layout settings from Redux store
  const dataTheme = useSelector((state: any) => state.themeSetting.dataTheme);
  const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout);

  // State for notification dropdown visibility
  const [notificationVisible, setNotificationVisible] = useState(false);

  // Create a ref for the notification dropdown
  const notificationRef = useRef<HTMLDivElement>(null);

  // Get mobile sidebar state from Redux store
  const mobileSidebar = useSelector(
    (state: any) => state.sidebarSlice.mobileSidebar
  );

  // Get user role and current user info
  const userRole = useSelector(selectUserRole) || "";
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = userRole === "superadmin";
  const isCenterAdmin = userRole === "centeradmin";

  // Get center data if user is center admin
  const { data: centerData } = useGetCenterByIdQuery(
    { id: String(currentUser?.center_id || "") },
    { skip: !isCenterAdmin || !currentUser?.center_id }
  );

  const centerName = centerData?.data?.center?.name;

  // Get center ID for marking notifications as read
  const centerId = currentUser?.center_id;

  // Setup mark as read mutation
  const [markAsRead] = useMarkNotificationAsReadMutation();

  // Split the notifications fetching into two hooks:
  // 1. One for checking unread notifications (always running)
  // 2. One for the dropdown content (only when visible)

  // This hook always runs to check for unread notifications
  const { data: unreadCheckData, isLoading: isCheckingUnread } =
    useGetAllNotificationsQuery(
      {
        size: 5, // Minimal data, we just need to know if there are any unread
        page: 1,
      },
      {
        // Only skip for superadmin
        skip: isSuperAdmin,
        // Poll periodically to check for new notifications
        pollingInterval: 150000, // Check every 2.5 minutes
      }
    );

  // This hook only runs when the dropdown is open
  const {
    data: notificationsData,
    isLoading,
    isError,
    refetch,
  } = useGetAllNotificationsQuery(
    {
      size: 5, // Limit to 5 notifications in header
      page: 1,
      created: false,
    },
    {
      // Only fetch when dropdown is visible
      skip: isSuperAdmin || !notificationVisible,
      // Refetch when dropdown opens
      refetchOnMountOrArgChange: true,
    }
  );

  // Check if there are any unread notifications
  const notifications = notificationsData?.data?.items || [];

  const visibleNotifications = notifications.filter(
    (notification: any) => notification.isPublished && !notification.isDeleted
  );

  // Use unreadCheckData to determine if there are unread notifications
  const hasUnreadNotifications =
    unreadCheckData?.data?.items?.some(
      (notification) => notification.isPublished && !notification.read_at
    ) || false;
  // Format timestamp to relative time with IST adjustment
  const formatTimeAgo = (timestamp: string) => {
    if (!timestamp) return "";

    // Parse the timestamp (coming in as UTC)
    const date = new Date(timestamp);

    // Calculate time difference in seconds
    const now = new Date();

    // First add IST offset (5:30 hours) to the UTC date
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);

    // Calculate the time difference between now and the IST-adjusted date
    const seconds = Math.floor((now.getTime() - istDate.getTime()) / 1000);

    if (seconds < 0) {
      // If seconds is negative, it means the adjusted time is in the future relative to now
      // This can happen due to timezone differences or server/client clock mismatch
      return "Just now";
    }

    if (seconds < 60) return `${seconds} second${seconds !== 1 ? "s" : ""} ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;

    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;

    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? "s" : ""} ago`;
  };

  // Handle marking a notification as read
  const handleMarkAsRead = async (notificationId: number) => {
    if (!centerId || !notificationId) return;

    try {
      const response = await markAsRead({
        notification_id: notificationId,
        center_id: centerId,
      }).unwrap();

      if (response.success) {
        // Refetch notifications to update the UI
        refetch();
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Add click listener to close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node) &&
        notificationVisible
      ) {
        setNotificationVisible(false);
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationVisible]);

  // Toggle mobile sidebar visibility
  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar));
  };

  // Handlers for sidebar hover effects
  const onMouseEnter = () => {
    dispatch(setExpandMenu(true));
  };
  const onMouseLeave = () => {
    dispatch(setExpandMenu(false));
  };

  // Toggle between mini and default layout
  const handleToggleMiniSidebar = () => {
    if (dataLayout === "mini_layout") {
      dispatch(setDataLayout("default_layout"));
      localStorage.setItem("dataLayout", "default_layout");
    } else {
      dispatch(toggleMiniSidebar());
    }
  };

  // Diagonal slash — glowing band sweeps top-left → bottom-right, revealing new theme
  const performSlashTransition = useCallback((nextTheme: string) => {
    dispatch(setDataTheme(nextTheme));

    const W = window.innerWidth;
    const H = window.innerHeight;
    const dpr = window.devicePixelRatio || 1;

    const canvas = document.createElement("canvas");
    // Physical pixels — sharp on all screens including Retina/mobile
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.cssText = `position:fixed;top:0;left:0;width:${W}px;height:${H}px;z-index:99999;pointer-events:none`;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr); // all drawing in CSS pixels from here

    const isDark = nextTheme === "dark_data_theme";
    const oldBg   = isDark ? "#f4f6fa" : "#0f0e1a";
    const glowClr = isDark ? "#3D5EE1" : "#7c6bff";

    // Slash band width — proportional so it looks good on any screen size
    const SLASH_W = Math.min(W, H) * 0.14;

    // Duration scales with diagonal length so speed feels consistent
    const diag = Math.hypot(W, H);
    const DURATION = Math.min(900, Math.max(550, diag * 0.55));

    let startTime: number | null = null;

    const draw = (ts: number) => {
      if (!startTime) startTime = ts;
      const t = Math.min((ts - startTime) / DURATION, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic

      const total = W + H;
      const leadX = eased * (total + SLASH_W) - H;

      ctx.clearRect(0, 0, W, H);

      // Old-theme fill — area not yet wiped
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(leadX,       0);
      ctx.lineTo(W + 10,      0);
      ctx.lineTo(W + 10,      H + 10);
      ctx.lineTo(leadX - H,   H + 10);
      ctx.closePath();
      ctx.fillStyle = oldBg;
      ctx.fill();
      ctx.restore();

      // Glowing edge band
      const x0 = leadX - SLASH_W;
      const x1 = leadX;
      const grad = ctx.createLinearGradient(x0, 0, x1, 0);
      grad.addColorStop(0,    "transparent");
      grad.addColorStop(0.3,  glowClr + "44");
      grad.addColorStop(0.75, glowClr + "cc");
      grad.addColorStop(1,    "#ffffff99");

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x0,     0);
      ctx.lineTo(x1,     0);
      ctx.lineTo(x1 - H, H);
      ctx.lineTo(x0 - H, H);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.restore();

      if (t < 1) {
        requestAnimationFrame(draw);
      } else {
        canvas.remove();
      }
    };

    requestAnimationFrame(draw);
  }, [dispatch]);

  // Toggle between light and dark theme
  const handleToggleClick = () => {
    const nextTheme = dataTheme === "default_data_theme" ? "dark_data_theme" : "default_data_theme";
    performSlashTransition(nextTheme);
  };

  // Get current location for routing
  const location = useLocation();

  // Toggle notification panel visibility
  const toggleNotification = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    setNotificationVisible(!notificationVisible);
  };

  // State and handler for fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => { });
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => { });
        }
        setIsFullscreen(false);
      }
    }
  };

  const handleLogout = async () => {
    try {
      const response = await logoutMutation().unwrap();
      dispatch(logout());
      return response;
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Truncate content to a certain length for preview
  const truncateContent = (content: string, maxLength: number = 80) => {
    if (!content) return "";
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  // Navigate to notification view and mark as read
  const navigateToNotification = (notificationId: number) => {
    // Mark notification as read
    handleMarkAsRead(notificationId);

    // Close notification dropdown
    setNotificationVisible(false);
  };

  // Add this useEffect to inject the CSS for animations
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      /* Unique animated notification badge */
      .notification-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.15rem 0.5rem;
        font-size: 0.6rem;
        font-weight: 600;
        line-height: 1;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #fff;
        background: linear-gradient(135deg, #ff416c, #dc3545);
        border-radius: 2px 8px 2px 8px;
        margin-left: 0.7rem;
        box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
        position: relative;
        overflow: hidden;
        transform: perspective(100px) rotateX(0deg);
        transform-origin: bottom;
        animation: badge-float 3s ease-in-out infinite, badge-glow 2s ease-in-out infinite;
        font-family: "Brush Script MT", "Brush Script Std", "Lucida Calligraphy", "Lucida Handwriting", "Apple Chancery", cursive;
        font-style: italic;
      }
      
      .notification-badge:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.3) 50%, transparent 75%);
        animation: badge-shimmer 2.5s linear infinite;
        background-size: 200% 200%;
      }
      
      .notification-badge:after {
        content: "";
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 2px 8px 2px 8px;
        pointer-events: none;
        animation: badge-border-pulse 2s linear infinite;
      }
      
      @keyframes badge-float {
        0%, 100% { transform: perspective(100px) rotateX(0deg) translateY(0); }
        50% { transform: perspective(100px) rotateX(3deg) translateY(-2px); }
      }
      
      @keyframes badge-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      
      @keyframes badge-glow {
        0%, 100% { box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4); }
        50% { box-shadow: 0 2px 12px rgba(220, 53, 69, 0.7); }
      }
      
      @keyframes badge-border-pulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }

      /* Enhanced notification dot */
      .notification-status-dot {
        position: absolute; 
        top: 0;
        right: 0;
        width: 8px;
        height: 8px;
        background-color: #ff416c;
        border-radius: 50%;
        z-index: 1;
        border: 1px solid #fff;
        box-shadow: 0 0 4px #ff416c;
        animation: dot-pulse 2s infinite;
      }
      
      @keyframes dot-pulse {
        0%, 100% { transform: scale(1); box-shadow: 0 0 4px #ff416c; }
        50% { transform: scale(1.1); box-shadow: 0 0 8px #ff416c; }
      }

      /* Mobile menu badge */
      .mobile-user-menu .notification-badge {
        margin-left: auto;
        padding: 0.1rem 0.4rem;
        transform-origin: center;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      {/* Main Header Container */}
      <div className="header">
        {/* Logo Section - Contains main logo and mini sidebar toggle */}
        <div
          className="header-left active"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {/* Main Logo */}
          <Link to={routes.adminDashboard} className="logo logo-normal">
            <ImageWithBasePath src="assets/img/logo.png" alt="Logo" />
          </Link>
          {/* Small Logo for Mini Sidebar */}
          <Link to={routes.adminDashboard} className="logo-small">
            <ImageWithBasePath src="assets/img/logo-small.png" alt="Logo" />
          </Link>
          {/* Dark Theme Logo */}
          <Link to={routes.adminDashboard} className="dark-logo">
            <ImageWithBasePath src="assets/img/logo.png" alt="Logo" />
          </Link>
          {/* Sidebar Toggle Button */}
          <Link id="toggle_btn" to="#" onClick={handleToggleMiniSidebar}>
            <i className="ti ti-menu-deep" />
          </Link>
        </div>

        {/* Mobile Menu Toggle Button */}
        <Link
          id="mobile_btn"
          className="mobile_btn"
          to="#sidebar"
          onClick={toggleMobileSidebar}
        >
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>

        {/* Header Right Section - Contains search, notifications, and user menu */}
        <div className="header-user">
          <div className="nav user-menu">
            {/* Search Bar */}
            <div className="nav-item nav-search-inputs me-auto">
              <div className="top-nav-search">
                <Link to="#" className="responsive-search">
                  <i className="fa fa-search" />
                </Link>
                <form action="#" className="dropdown">
                  <div className="searchinputs" id="dropdownMenuClickable">
                    <input type="text" placeholder="Search" />
                    <div className="search-addon">
                      <button type="submit">
                        <i className="ti ti-search" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Header Actions Section */}
            <div className="d-flex align-items-center">
              {/* Theme Toggle Button */}
              <div className="pe-1">
                {!location.pathname.includes("layout-dark") && (
                  <Link
                    onClick={handleToggleClick}
                    to="#"
                    id="dark-mode-toggle"
                    className="dark-mode-toggle activate btn btn-outline-light bg-white btn-icon me-1"
                  >
                    <i
                      className={
                        dataTheme === "default_data_theme"
                          ? "ti ti-moon"
                          : "ti ti-brightness-up"
                      }
                    />
                  </Link>
                )}
              </div>

              {/* Notifications Dropdown - Hide for superadmin */}
              {!isSuperAdmin && (
                <div
                  className={`pe-1 ${notificationVisible ? "notification-item-show" : ""
                    }`}
                  id="notification_item"
                  ref={notificationRef}
                >
                  <Link
                    onClick={toggleNotification}
                    to="#"
                    className="btn btn-outline-light bg-white btn-icon position-relative me-1"
                    id="notification_popup"
                  >
                    <i
                      className={`ti ti-bell ${!isCheckingUnread && hasUnreadNotifications
                        ? "bell-shake"
                        : ""
                        }`}
                    />
                    {!isCheckingUnread && hasUnreadNotifications && (
                      <span className="notification-status-dot" />
                    )}
                  </Link>
                  {notificationVisible && (
                    <div
                      className="dropdown-menu dropdown-menu-end notification-dropdown shadow-lg border-0 p-0"
                      style={{ width: "350px" }}
                    >
                      {/* Header section with title */}
                      <div className="p-3 border-bottom bg-light rounded-top">
                        <div className="d-flex align-items-center justify-content-between">
                          <h5 className="notification-title mb-0 text-dark">
                            Notifications
                          </h5>
                        </div>
                      </div>

                      {/* Notification content */}
                      <div>
                        {isLoading ? (
                          <div className="text-center py-4">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </div>
                        ) : isError ? (
                          <div className="text-center py-4">
                            <div className="alert alert-danger d-inline-block m-0">
                              Failed to load notifications
                            </div>
                          </div>
                        ) : visibleNotifications.length === 0 ? (
                          <div className="text-center py-4">
                            <i className="ti ti-bell-off fs-2 text-muted mb-2 d-block"></i>
                            <p className="text-muted mb-0">
                              No notification available
                            </p>
                          </div>
                        ) : (
                          <div className="p-0">
                            {visibleNotifications
                              .slice(0, 5)
                              .map((notification, index, array) => {
                                const isRead = !!notification.read_at;
                                const hasAttachments =
                                  notification.attachments?.filter(
                                    (att) => att && att.trim() !== ""
                                  ).length > 0;

                                return (
                                  <div
                                    key={notification.id}
                                    className={`notification-item ${!isRead ? "bg-light-100" : ""
                                      } p-3 ${index !== array.length - 1
                                        ? "border-bottom"
                                        : ""
                                      }`}
                                  >
                                    <Link
                                      to={routes.notificationView.replace(
                                        ":id",
                                        String(notification.id)
                                      )}
                                      onClick={() =>
                                        navigateToNotification(notification.id)
                                      }
                                      className="text-decoration-none"
                                    >
                                      <div className="d-flex flex-column">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                          <div className="d-flex align-items-center">
                                            <h6
                                              className={`mb-0 ${!isRead
                                                ? "fw-semibold"
                                                : "fw-normal"
                                                }`}
                                              style={{ fontSize: "0.9rem" }}
                                            >
                                              {notification.title}
                                            </h6>
                                            {!isRead && (
                                              <span className="notification-badge">
                                                New
                                              </span>
                                            )}
                                          </div>
                                          <small
                                            className="text-muted flex-shrink-0 ms-2"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {formatTimeAgo(
                                              notification.publishedOn
                                            )}
                                          </small>
                                        </div>
                                        <p
                                          className={`text-muted mb-0 small ${!isRead ? "fw-medium" : ""
                                            }`}
                                          style={{
                                            lineHeight: "1.4",
                                            fontSize: "0.8rem",
                                          }}
                                        >
                                          {truncateContent(
                                            notification.content,
                                            80
                                          )}
                                        </p>
                                        {hasAttachments && (
                                          <div className="mt-1">
                                            <small
                                              className="text-primary"
                                              style={{ fontSize: "0.7rem" }}
                                            >
                                              <i className="ti ti-paperclip me-1"></i>
                                              {
                                                notification.attachments.filter(
                                                  (att) =>
                                                    att && att.trim() !== ""
                                                ).length
                                              }{" "}
                                              attachment(s)
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    </Link>
                                  </div>
                                );
                              })}

                            {/* Add a message showing the number of additional notifications */}
                            {visibleNotifications.length > 5 && (
                              <div className="text-center py-2 border-top">
                                <p className="text-muted mb-0 small">
                                  {visibleNotifications.length - 5}{" "}
                                  more notifications
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Footer with action buttons */}
                      <div className="p-3 border-top">
                        <div className="d-grid gap-2 d-md-flex">
                          <Link
                            to="#"
                            className="btn btn-light w-100"
                            onClick={() => setNotificationVisible(false)}
                          >
                            <i className="ti ti-x me-1"></i> Close
                          </Link>
                          {visibleNotifications.length > 0 && (
                            <Link
                              to={routes.activity}
                              className="btn btn-primary w-100"
                              onClick={() => setNotificationVisible(false)}
                            >
                              <i className="ti ti-list me-1"></i> View All
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Fullscreen Toggle Button */}
              <div className="pe-1">
                <Link
                  onClick={toggleFullscreen}
                  to="#"
                  className="btn btn-outline-light bg-white btn-icon me-1"
                  id="btnFullscreen"
                >
                  <i className="ti ti-maximize" />
                </Link>
              </div>

              {/* Lock Screen Button */}
              <div className="pe-1">
                <LockScreenButton className="btn btn-outline-light bg-white btn-icon me-1">
                  <i className="ti ti-lock" />
                </LockScreenButton>
              </div>

              {/* User Profile Dropdown */}
              <div className="dropdown ms-1">
                <Link
                  to="#"
                  className="dropdown-toggle d-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <span className="avatar avatar-md rounded">
                    <ImageWithBasePath
                      src="assets/img/profiles/avatar-27.jpg"
                      alt="Img"
                      className="img-fluid"
                    />
                  </span>
                </Link>
                <div className="dropdown-menu">
                  <div className="d-block">
                    <div className="d-flex align-items-center p-2">
                      <span className="avatar avatar-md me-2 online avatar-rounded">
                        <ImageWithBasePath
                          src="assets/img/profiles/avatar-27.jpg"
                          alt="img"
                        />
                      </span>
                      <div>
                        <h6>
                          {isCenterAdmin && centerName
                            ? centerName
                            : currentUser?.username}
                        </h6>
                        <p className="text-primary mb-0">{userRole}</p>
                      </div>
                    </div>
                    <hr className="m-0" />
                    {userRole === "centeradmin" && (
                      <>
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-2"
                          to={routes.centerProfile}
                        >
                          <i className="ti ti-building me-2" />
                          Center Profile
                        </Link>
                        <hr className="m-0" />
                      </>
                    )}
                    <Link
                      className="dropdown-item text-danger d-inline-flex align-items-center p-2"
                      to=""
                      onClick={handleLogout}
                    >
                      <i className="ti ti-login me-2" />
                      Logout
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown with notification indicator */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle position-relative"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
            {/* Show notification dot for non-superadmins */}
            {!isSuperAdmin && !isCheckingUnread && hasUnreadNotifications && (
              <span
                className="notification-status-dot"
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "10px",
                }}
              />
            )}
          </Link>
          <div className="dropdown-menu dropdown-menu-end">
            {/* Theme toggle for mobile */}
            {!location.pathname.includes("layout-dark") && (
              <Link
                className="dropdown-item d-flex align-items-center"
                onClick={handleToggleClick}
                to="#"
              >
                <i
                  className={`ti ${dataTheme === "default_data_theme"
                    ? "ti-moon"
                    : "ti-brightness-up"
                    } me-2`}
                />
                {dataTheme === "default_data_theme"
                  ? "Dark Mode"
                  : "Light Mode"}
              </Link>
            )}

            {/* Notifications for mobile - hide for superadmin */}
            {!isSuperAdmin && (
              <Link
                className="dropdown-item d-flex align-items-center"
                to={routes.activity}
              >
                <i className="ti ti-bell me-2" />
                Notifications
                {!isCheckingUnread && hasUnreadNotifications && (
                  <span className="notification-badge ms-auto">New</span>
                )}
              </Link>
            )}

            {/* Fullscreen toggle for mobile */}
            <Link
              className="dropdown-item d-flex align-items-center"
              onClick={toggleFullscreen}
              to="#"
            >
              <i className="ti ti-maximize me-2" />
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </Link>

            {/* Lock Screen for mobile */}
            <LockScreenButton
              className="dropdown-item d-flex align-items-center"
            >
              <i className="ti ti-lock me-2" />
              Lock Screen
            </LockScreenButton>

            {/* Existing menu items */}
            <hr className="dropdown-divider" />
            {userRole === "centeradmin" && (
              <Link
                className="dropdown-item d-flex align-items-center"
                to={routes.centerProfile}
              >
                <i className="ti ti-building me-2" />
                Center Profile
              </Link>
            )}
            <Link
              className="dropdown-item d-flex align-items-center text-danger"
              to=""
              onClick={handleLogout}
            >
              <i className="ti ti-login me-2" />
              Logout
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
